import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useEffect, useState } from 'react'
import { ListDownload } from 'player/src/components/download-task-drop'
import { useAudioEngine } from '../../../components/core-audio-ui-unified/use-audio-engine'
import { FilesDownload } from '../../../components/tasks/download-task-drop'
import { off, on } from '../../../lib/events'
import {
  OperationBeatChordsResult,
  OperationSplitResult,
  Task
} from '../../../types'
import { useDownloadTaskUtils } from '../use-download-task-utils'
import { useGenerateMix } from '../use-generate-mix'
import { TrackAudio } from '../use-track-split'
import {
  getBucketDownload,
  getItemToDownload,
  getLinkDownload
} from '../use-download-task-split/utils'
import { MixEffects } from '../use-generate-mix-new'
import { TrackAudioPlayer } from '../use-download-task-utils/types'

export interface UseDownloadSingletrack {
  mixData: TrackAudioPlayer[]
  files: FilesDownload[]
  setMixData(tracks: TrackAudioPlayer[]): void
}

export interface Props {
  task: Task
  tracks: TrackAudio[]
  isDownloadWavEnabled: boolean
  effects?: MixEffects
  newPlayer?: boolean
  type: 'mastered' | 'denoised'
  typeOp?: 'mastering' | 'denoiser'
  onGenerateMixNewPlayer?: (i: any) => void
}

export const useDownloadSingletrack = ({
  task,
  tracks,
  isDownloadWavEnabled,
  effects,
  newPlayer,
  typeOp,
  type,
  onGenerateMixNewPlayer
}: Props): UseDownloadSingletrack => {
  const { i18n } = useLingui()
  const [mixData, setMixData] = useState<TrackAudioPlayer[]>([])
  const [result, setResult] = useState<OperationSplitResult | null>(null)
  const [typeOperation, setTypeOperation] = useState(null)
  useState<OperationBeatChordsResult | null>(null)
  const [files, setFiles] = useState<FilesDownload[]>([])
  const [selectedMetronomeSpeed, setSelectedMetronomeSpeed] =
    useState<string>('metronome')
  const {
    getTypeFile,
    onDownloadSong,
    getStringTypeFile,
    orderFormatDownload
  } = useDownloadTaskUtils({
    taskId: task.id
  })

  const { onGenerateMix } = useGenerateMix({
    name: task?.file?.name,
    tracks,
    mixData,
    effects
  })

  const channelsState = useAudioEngine((p) => {
    const channels1: any = []
    Object.entries(p.channels).forEach(([key]) => {
      channels1.push({
        id: p.channels[key].id,
        pan: p.channels[key].pan,
        volume: p.channels[key].realVolume ?? p.channels[key].volume,
        isSolo: p.channels[key].isSolo,
        isMuted: p.channels[key].isMuted,
        playbackRate: p.channels[key].playbackRate,
        pitchShiftCents: p.channels[key].pitchShiftCents
      })
    })
    return channels1
  })

  const onChangeChannels = useCallback(() => {
    const channelsData: TrackAudioPlayer[] = channelsState?.map((c: any) => ({
      name: c.id,
      value: {
        muted: c.isMuted,
        solo: c.isSolo,
        pan: {
          value: c.pan
        },
        volume: {
          value: c.volume * 50 - 50
        }
      }
    }))
    setMixData(channelsData)
  }, [channelsState])

  const onChangeMetronome = useCallback(({ detail }: any): void => {
    setSelectedMetronomeSpeed(detail)
  }, [])

  useEffect(() => {
    on('player:channel-changed-2', onChangeChannels)
    on('player:metronome-changed', onChangeMetronome)

    return () => {
      off('player:channel-changed-2', onChangeChannels)
      off('player:metronome-changed', onChangeMetronome)
    }
  }, [onChangeChannels, onChangeMetronome])

  useEffect(() => {
    if (!result?.files) {
      return
    }

    const resultFiles = {
      ...result.files,
      ...task.operations.find((op) =>
        op.name.toLocaleLowerCase().includes('beatschords')
      )?.result?.files
    }

    const list: FilesDownload[] = []
    const formats = ['mp3', 'm4a', 'wav']

    formats.forEach((format: string) => {
      const item: FilesDownload = {
        format,
        title: format.toUpperCase(),
        locked: !isDownloadWavEnabled && format === 'wav',
        items: []
      }

      /** Download directly file mastered/denoised */
      const listSeparateTracks: ListDownload = {
        title: i18n._(t`task.label.mastered.tracks`),
        items: []
      }
      const stem = type
      const bucket = getBucketDownload({
        resultFiles,
        format,
        stem
      })
      const { link, filename } = getLinkDownload({ bucket, format, stem, task })

      listSeparateTracks.items.push(
        getItemToDownload({
          i18n,
          stem,
          link,
          bucket,
          title:
            format === 'm4a'
              ? '320k'
              : format === 'wav'
              ? '24 bit'
              : i18n._(t`actions.download`),
          onClick: (e) => {
            e.preventDefault()
            onDownloadSong({
              url: link,
              title: `${task?.file?.name} (${stem})`,
              filename,
              format
            })
          }
        })
      )

      item.items.push(listSeparateTracks)

      /** Define button 'Generate Mix' */
      // let hasWavHighRes = true

      // stemsToDownload.forEach((stem: string) => {
      //   if (format === 'wav' && !resultFiles[`${stem}HighRes`]) {
      //     hasWavHighRes = false
      //   }
      // })

      // item.items.push({
      //   title:
      //     typeOp === 'mastering'
      //       ? i18n._(t`task.label.mastered.tracks`)
      //       : i18n._(t`task.label.denoised.tracks`),
      //   items: [
      //     {
      //       disabled: false,
      //       iconName: 'download',
      //       light: true,
      //       transparent: true,
      //       title: i18n._(t`download`),
      //       onClick: () => {
      //         const props = {
      //           format: item.format,
      //           hasWavHighRes,
      //           onDownloadSong,
      //           type: typeOperation,
      //           bucket: task.file.location || ''
      //         }

      //         if (newPlayer && onGenerateMixNewPlayer) {
      //           onGenerateMixNewPlayer({ ...props })
      //         } else {
      //           onGenerateMix({ ...props })
      //         }
      //       }
      //     }
      //   ]
      // })

      list.push(item)
    })

    setFiles(list)
  }, [
    task,
    type,
    tracks,
    typeOp,
    newPlayer,
    isDownloadWavEnabled,
    i18n,
    result,
    typeOperation,
    onDownloadSong,
    onGenerateMix,
    onGenerateMixNewPlayer,
    getStringTypeFile,
    getTypeFile,
    orderFormatDownload,
    selectedMetronomeSpeed
  ])

  useEffect(() => {
    const operationSeparate = task.operations.find(({ name: operationName }) =>
      operationName.toLowerCase().includes(typeOp as any)
    )
    const resultOp = operationSeparate?.result
    const typeOperationSeparate = typeOp

    if (resultOp && resultOp.files) {
      setResult(resultOp)
      setTypeOperation(typeOperationSeparate as any)
    }
  }, [task, typeOp])

  return {
    files,
    mixData,
    setMixData
  }
}
